import React, { memo, useState, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { areEqual } from 'Utils/equalityChecks';
import { Modal } from 'Components/Modal';
import { Label } from 'Components/Label';
import { PurpleButton } from 'Components/Button';
import { CatalogItem, RegisterAssetPayload } from 'Containers/SerializedEquipment/types';

import formClasses from 'Themes/form/form.module.css';
import classes from './addSerializedEquipmentModal.module.css';

interface Props {
  isOpen: boolean;
  catalog: CatalogItem[];
  submitting: boolean;
  submitError?: string | null;
  onSubmit: (payload: RegisterAssetPayload) => void;
  onClose: (e: any) => void;
}

const EMPTY_FORM = {
  catalogUuid: '',
  manufacturer: '',
  model: '',
  serialNumber: '',
  assetTag: '',
  vendor: '',
  purchaseDate: '',
  warrantyExpiresAt: '',
  purchasePrice: '',
  rentalDayRate: '',
  note: '',
};

type FormState = typeof EMPTY_FORM;

interface FieldProps {
  id: string;
  label: string;
  type?: string;
  value: string;
  onChange: (e: any) => void;
}

function Field({ id, label, type = 'text', value, onChange }: FieldProps) {
  return (
    <div className={classes.field}>
      <Label ariaLabel={label} className={formClasses.label} htmlFor={id}>
        {label}
      </Label>
      <input
        id={id}
        type={type}
        min={type === 'number' ? 0 : undefined}
        step={type === 'number' ? '0.01' : undefined}
        className="form-control"
        value={value}
        onChange={onChange}
        autoComplete="off"
        data-testid={id}
      />
    </div>
  );
}

Field.defaultProps = { type: 'text' };

function AddSerializedEquipmentModal({ isOpen, catalog, submitting, submitError, onSubmit, onClose }: Props) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  // Mint the idempotency key once when the modal opens and reuse it for every submit attempt in this
  // session. If the server succeeds but the response is lost, a retry replays the same key so the
  // backend dedupes it instead of registering a second physical unit. A fresh open gets a fresh key.
  const [idempotencyKey, setIdempotencyKey] = useState('');

  useEffect(() => {
    if (isOpen) {
      setIdempotencyKey(uuidv4());
    } else {
      setForm(EMPTY_FORM);
    }
  }, [isOpen]);

  const setField = useCallback(
    (field: keyof FormState) => (e: any) => {
      const { value } = e.target;
      setForm((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const onSubmitForm = useCallback(
    (e: any) => {
      e.preventDefault();
      const selected = catalog.find((item) => item.catalog_uuid === form.catalogUuid);
      if (!selected) return;

      const payload: RegisterAssetPayload = {
        catalog_uuid: selected.catalog_uuid,
        name: selected.name,
        idempotency_key: idempotencyKey,
      };

      const text: Array<[keyof RegisterAssetPayload, string]> = [
        ['manufacturer', form.manufacturer],
        ['model', form.model],
        ['serial_number', form.serialNumber],
        ['asset_tag', form.assetTag],
        ['vendor', form.vendor],
        ['note', form.note],
        ['purchase_date', form.purchaseDate],
        ['warranty_expires_at', form.warrantyExpiresAt],
      ];
      text.forEach(([key, value]) => {
        if (value.trim()) {
          (payload as any)[key] = value.trim();
        }
      });

      const numeric: Array<[keyof RegisterAssetPayload, string]> = [
        ['purchase_price', form.purchasePrice],
        ['rental_day_rate', form.rentalDayRate],
      ];
      numeric.forEach(([key, value]) => {
        if (value.trim() !== '' && !Number.isNaN(Number(value))) {
          (payload as any)[key] = Number(value);
        }
      });

      onSubmit(payload);
    },
    [catalog, form, idempotencyKey, onSubmit]
  );

  const canSubmit = !!form.catalogUuid && !submitting;

  return (
    <Modal
      id="add-serialized-equipment-modal"
      classes={classes}
      title="Add Serialized Equipment"
      isOpen={isOpen}
      modalHeader
      dataBsBackdrop="static"
      dataBsKeyboard="false"
      modalCloseClick={onClose}
    >
      <form
        className={`${formClasses.formBase} ${classes.container}`}
        noValidate
        action="#"
        data-testid="add-serialized-form"
        onSubmit={onSubmitForm}
      >
        <h6 className={classes.sectionTitle}>Identity</h6>
        <div className={classes.field}>
          <Label ariaLabel="Equipment Type" className={formClasses.label} htmlFor="serializedType">
            Equipment Type
          </Label>
          <select
            id="serializedType"
            className="form-select"
            value={form.catalogUuid}
            aria-label="Equipment type"
            onChange={setField('catalogUuid')}
          >
            <option value="">Select a type…</option>
            {catalog.map((item) => (
              <option key={item.catalog_uuid} value={item.catalog_uuid}>
                {item.name}
              </option>
            ))}
          </select>
        </div>
        <Field id="manufacturer" label="Manufacturer" value={form.manufacturer} onChange={setField('manufacturer')} />
        <Field id="model" label="Model" value={form.model} onChange={setField('model')} />
        <Field id="serialNumber" label="Serial Number" value={form.serialNumber} onChange={setField('serialNumber')} />
        <Field id="assetTag" label="Asset Tag" value={form.assetTag} onChange={setField('assetTag')} />

        <h6 className={classes.sectionTitle}>Financial / rental</h6>
        <Field id="vendor" label="Vendor" value={form.vendor} onChange={setField('vendor')} />
        <Field id="purchaseDate" label="Purchase Date" type="date" value={form.purchaseDate} onChange={setField('purchaseDate')} />
        <Field
          id="purchasePrice"
          label="Purchase Price"
          type="number"
          value={form.purchasePrice}
          onChange={setField('purchasePrice')}
        />
        <Field
          id="warrantyExpiresAt"
          label="Warranty Expires"
          type="date"
          value={form.warrantyExpiresAt}
          onChange={setField('warrantyExpiresAt')}
        />
        <Field
          id="rentalDayRate"
          label="Rental Day Rate"
          type="number"
          value={form.rentalDayRate}
          onChange={setField('rentalDayRate')}
        />
        <Field id="note" label="Note" value={form.note} onChange={setField('note')} />

        {submitError && (
          <p className="text-danger mt-2 mb-0" role="alert" data-testid="register-error">
            {submitError}
          </p>
        )}

        <div className={`modal-footer ${classes.modalFooter}`}>
          <PurpleButton type="button" onClick={onClose} className={classes.cancelButton}>
            Cancel
          </PurpleButton>
          <PurpleButton type="submit" disabled={!canSubmit} className={classes.createButton}>
            Register
          </PurpleButton>
        </div>
      </form>
    </Modal>
  );
}

AddSerializedEquipmentModal.defaultProps = {
  submitError: null,
};

const AddSerializedEquipmentModalMemo = memo(AddSerializedEquipmentModal, areEqual);

export { AddSerializedEquipmentModalMemo as AddSerializedEquipmentModal };
